/**
 * posterarte.collection.js
 * POSTERARTE — Collection page behaviors
 * AJAX filtering via Shopify Section Rendering API (no page reloads).
 */
(function () {
  'use strict';

  // Sections refreshed on filter/sort/paginate (must match keys in collection.posterarte.json)
  let REFRESH_SECTIONS = ['posterarte-grid', 'posterarte-filters']; // overwritten by discoverSections()
  const WISHLIST_KEY = 'pa-wishlist';

  // NOTE: do NOT touch document.body here. When this script is loaded from
  // <head> via {{ ... | script_tag }}, document.body is null at this point.
  // Any TypeError thrown here would abort the IIFE and prevent every
  // event listener below (drawer, filters, sort, FAQ, wishlist) from
  // ever being registered. We tag the body inside init() instead.

  /* ──────────────────────────────────────────────
     STICKY FILTER BAR
     ────────────────────────────────────────────── */
  function initSticky() {
    const hero = document.querySelector('.pa-col-hero');
    if (!hero) return;
    const observer = new IntersectionObserver(function (entries) {
      const filterBar = document.querySelector('.pa-col-filters');
      if (!filterBar) return;
      entries.forEach(function (entry) {
        if (entry.isIntersecting) filterBar.classList.remove('is-stuck');
        else filterBar.classList.add('is-stuck');
      });
    }, { threshold: 0 });
    observer.observe(hero);
  }

  /* ──────────────────────────────────────────────
     DRAWER
     ────────────────────────────────────────────── */
  function openDrawer() {
    const overlay = document.getElementById('pa-col-drawer-overlay');
    const panel   = document.getElementById('pa-col-drawer');
    if (overlay) overlay.classList.add('is-open');
    if (panel) {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
    }
    if (document.body) document.body.classList.add('pa-col-no-scroll');
    // Verify in next frame — Ella's section JS sometimes reverts these synchronously.
    requestAnimationFrame(function () {
      const p = document.getElementById('pa-col-drawer');
      if (!p) return;
      if (!p.classList.contains('is-open')) {
        p.classList.add('is-open');
        p.setAttribute('aria-hidden', 'false');
      }
    });
  }
  function closeDrawer() {
    const overlay = document.getElementById('pa-col-drawer-overlay');
    const panel   = document.getElementById('pa-col-drawer');
    if (overlay) overlay.classList.remove('is-open');
    if (panel) {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    }
    if (document.body) document.body.classList.remove('pa-col-no-scroll');
  }

  /* ──────────────────────────────────────────────
     FAQ — first item open by default; delegation handles clicks
     ────────────────────────────────────────────── */
  function initFAQ() {
    const first = document.querySelector('.pa-col-faq__item');
    if (first && !first.classList.contains('is-open')) {
      first.classList.add('is-open');
    }
  }

  /* ──────────────────────────────────────────────
     WISHLIST (localStorage)
     Stored under WISHLIST_KEY as an array of {id, handle} objects.
     getWishlist() returns a Map keyed by product id (string) so the
     handle travels with each entry (needed by the wishlist page).
     ────────────────────────────────────────────── */
  function getWishlist() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      const map = new Map();
      if (Array.isArray(arr)) {
        arr.forEach(function (item) {
          // Tolerate the legacy format (array of bare id strings)
          if (typeof item === 'string') {
            map.set(item, { id: item, handle: '' });
          } else if (item && item.id != null) {
            map.set(String(item.id), { id: String(item.id), handle: item.handle || '' });
          }
        });
      }
      return map;
    } catch (_e) { return new Map(); }
  }
  function saveWishlist(map) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(Array.from(map.values()))); }
    catch (_e) { /* storage unavailable */ }
    // Con sesion, posterarte-favoritos.js sube la lista a la cuenta.
    try { if (window.paFavoritos) window.paFavoritos.cambio(); } catch (_e) { /* nada */ }
  }
  function setHeart(btn, filled) {
    /* El color lo decide el CSS: aqui solo se marca el estado. Antes se
       pintaba en linea y esos estilos ganaban a cualquier hoja. */
    btn.classList.toggle('is-saved', !!filled);
    btn.setAttribute('aria-pressed', filled ? 'true' : 'false');
    btn.style.filter = '';
    const svg = btn.querySelector('svg');
    if (svg) { svg.style.fill = ''; svg.style.stroke = ''; }
  }
  function paintWishlistHearts() {
    const list = getWishlist();
    document.querySelectorAll('.pa-col-card__wishlist[data-product-id]').forEach(function (btn) {
      setHeart(btn, list.has(btn.dataset.productId));
    });
  }

  /* ──────────────────────────────────────────────
     HEADER COUNTER — POSTERARTE Dawn header wishlist badge
     Updates .pa-wishlist-count in posterarte-header.liquid. Hidden at 0.
     ────────────────────────────────────────────── */
  function updateDawnCounter() {
    const count = getWishlist().size;
    document.querySelectorAll('.pa-wishlist-count').forEach(function (badge) {
      if (count > 0) {
        badge.textContent = count;
        badge.style.display = '';
      } else {
        badge.textContent = '0';
        badge.style.display = 'none';
      }
    });
  }

  /* ──────────────────────────────────────────────
     QUICK ADD TO CART
     ────────────────────────────────────────────── */
  async function quickAddToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const card = btn.closest('.pa-col-card');
    btn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('Cart add HTTP ' + res.status);
      // Confirmation state
      if (card) card.classList.add('pa-col-card--added');
      // Notify Ella's theme so its cart drawer / counter updates
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:cart:add', { bubbles: true }));
      // Reset after 2 s
      setTimeout(function () {
        if (card) card.classList.remove('pa-col-card--added');
        btn.disabled = false;
      }, 2000);
    } catch (_err) {
      btn.disabled = false;
    }
  }

  /* ──────────────────────────────────────────────
     SECTION ID DISCOVERY
     Reads the actual shopify-section-* IDs Shopify assigned in this
     theme instance. Hardcoded IDs in REFRESH_SECTIONS only match if
     the live theme uses identical keys to our collection.posterarte.json.
     ────────────────────────────────────────────── */
  function discoverSections() {
    const gridRoot = document.querySelector('.pa-col-grid-section, .pa-col-empty');
    const gridWrapper = gridRoot && gridRoot.closest('[id^="shopify-section-"]');

    const filtersRoot = document.querySelector('.pa-col-filters');
    const filtersWrapper = filtersRoot && filtersRoot.closest('[id^="shopify-section-"]');

    const ids = [];
    if (gridWrapper)    ids.push(gridWrapper.id.replace('shopify-section-', ''));
    if (filtersWrapper) ids.push(filtersWrapper.id.replace('shopify-section-', ''));

    if (ids.length) REFRESH_SECTIONS = ids;
  }

  /* ──────────────────────────────────────────────
     AJAX SECTION RENDER — no page reload
     ────────────────────────────────────────────── */
  let inFlightCtrl = null;

  async function applyUrl(targetUrl) {
    if (!targetUrl) return;

    // Abort any in-flight request
    if (inFlightCtrl) inFlightCtrl.abort();
    inFlightCtrl = new AbortController();

    const scrollY = window.scrollY;
    document.body.classList.add('pa-col-filtering');

    let absolute;
    try {
      absolute = new URL(targetUrl, window.location.origin);
    } catch (_e) {
      document.body.classList.remove('pa-col-filtering');
      return;
    }

    const sectionsUrl = new URL(absolute.toString());
    sectionsUrl.searchParams.set('sections', REFRESH_SECTIONS.join(','));

    try {
      const res = await fetch(sectionsUrl.toString(), {
        signal: inFlightCtrl.signal,
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();

      const parser = new DOMParser();

      // Grid — use .pa-col-grid-section so empty state + pagination are included
      const gridId = REFRESH_SECTIONS.find(function (id) { return id.includes('posterarte-grid'); });
      if (gridId && data[gridId]) {
        const doc = parser.parseFromString(data[gridId], 'text/html');
        const newGrid     = doc.querySelector('.pa-col-grid-section');
        const currentGrid = document.querySelector('.pa-col-grid-section');
        if (newGrid && currentGrid) currentGrid.innerHTML = newGrid.innerHTML;
      }

      // Filters — updates active pills, inline strip, and drawer filter URLs
      const filtersId = REFRESH_SECTIONS.find(function (id) { return id.includes('posterarte-filters'); });
      if (filtersId && data[filtersId]) {
        const doc = parser.parseFromString(data[filtersId], 'text/html');

        // Filter bar (active pills, count badge, inline strip)
        const newFilters     = doc.querySelector('.pa-col-filters');
        const currentFilters = document.querySelector('.pa-col-filters');
        if (newFilters && currentFilters) currentFilters.innerHTML = newFilters.innerHTML;

        // Drawer — sibling of .pa-col-filters, must be updated separately so
        // filter pill URLs reflect the new active-filter state for stacking
        const newDrawer     = doc.querySelector('.pa-col-drawer');
        const currentDrawer = document.getElementById('pa-col-drawer');
        if (newDrawer && currentDrawer) currentDrawer.innerHTML = newDrawer.innerHTML;
      }

      // Update browser URL without navigation
      history.pushState({ paFilter: true }, '', absolute.pathname + absolute.search);

      // Maintain scroll position
      window.scrollTo(0, scrollY);

      // Re-paint dynamic state on the new DOM
      paintWishlistHearts();

    } catch (err) {
      if (err.name === 'AbortError') return;
      // Do NOT fall back to window.location — that defeats the no-reload promise.
      // Leave the page alone so the user can retry.
    } finally {
      document.body.classList.remove('pa-col-filtering');
      inFlightCtrl = null;
    }
  }

  /* ──────────────────────────────────────────────
     EVENT DELEGATION — capture phase so Ella's theme JS
     can't stopPropagation us out of the chain. Each match
     calls preventDefault + stopImmediatePropagation to
     guarantee no other listener (theme, link nav) runs.
     ────────────────────────────────────────────── */
  function killEvent(e) {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  }

  document.addEventListener('click', function (e) {
    // Drawer open
    const btn = e.target.closest('.pa-col-filters__btn');
    if (btn) {
      killEvent(e);
      const panel = document.querySelector('.pa-col-drawer');
      if (panel?.classList.contains('is-open')) {
        closeDrawer();
      } else {
        openDrawer();
      }
      return;
    }
    // Drawer close
    if (e.target.closest('[data-drawer-close], .pa-col-drawer__close')) {
      killEvent(e);
      closeDrawer();
      return;
    }
    // Filter URLs (drawer pills, color dots, active-pills, clear-all)
    // The drawer stays open so the user can stack multiple filters.
    const filterEl = e.target.closest('[data-filter-url]');
    if (filterEl) {
      killEvent(e);
      const url = filterEl.dataset.filterUrl || filterEl.getAttribute('href');
      applyUrl(url);
      return;
    }
    // Pagination — full page load (better for SEO than AJAX).
    // Set a flag so the new page knows to scroll to the grid after rendering.
    const pageA = e.target.closest('.pa-col-pagination__page, .pa-col-pagination__prev, .pa-col-pagination__next');
    if (pageA && pageA.tagName === 'A') {
      sessionStorage.setItem('scrollToGrid', 'true');
      // No killEvent — browser navigates normally to ?page=N
      return;
    }
    // Editorial CTA → AJAX filter (only when data-curated-url is present)
    const editorialCta = e.target.closest('.pa-col-editorial__cta[data-curated-url]');
    if (editorialCta) {
      killEvent(e);
      applyUrl(editorialCta.dataset.curatedUrl);
      return;
    }
    // Editorial anchor → smooth scroll to grid WITHOUT adding hash to URL.
    // Using scrollIntoView + preventDefault so Ella's hash-change listeners
    // never fire and cause the double-scroll back to the editorial section.
    const editorialAnchor = e.target.closest('.pa-col-editorial__cta[href="#pa-col-grid-section"]');
    if (editorialAnchor) {
      killEvent(e);
      const gridSection = document.getElementById('pa-col-grid-section');
      if (gridSection) {
        gridSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    // Filter group toggle (drawer accordion)
    const groupToggle = e.target.closest('[data-group-toggle]');
    if (groupToggle) {
      killEvent(e);
      const body = document.getElementById(groupToggle.getAttribute('aria-controls'));
      const expanded = groupToggle.getAttribute('aria-expanded') === 'true';
      groupToggle.setAttribute('aria-expanded', String(!expanded));
      if (body) body.hidden = expanded;
      return;
    }
    // FAQ accordion
    const faqQ = e.target.closest('.pa-col-faq__question');
    if (faqQ) {
      killEvent(e);
      faqQ.closest('.pa-col-faq__item')?.classList.toggle('is-open');
      return;
    }
    // Quick add to cart (single-variant products)
    const addBtn = e.target.closest('.pa-col-card__add-btn[data-variant-id]');
    if (addBtn) {
      killEvent(e);
      quickAddToCart(addBtn);
      return;
    }
    // Wishlist heart
    const wishBtn = e.target.closest('.pa-col-card__wishlist[data-product-id]');
    if (wishBtn) {
      killEvent(e);
      const id = wishBtn.dataset.productId;
      const handle = wishBtn.dataset.productHandle || '';
      const wl = getWishlist();
      const isAdding = !wl.has(id);
      if (isAdding) wl.set(id, { id: id, handle: handle }); else wl.delete(id);
      saveWishlist(wl);
      setHeart(wishBtn, wl.has(id));
      updateDawnCounter();
      return;
    }
    // Backdrop click closes drawer
    const overlay = e.target.closest('.pa-col-drawer-overlay');
    if (overlay && e.target === overlay) {
      closeDrawer();
      return;
    }
    // Click outside drawer panel closes it
    const panel = document.querySelector('.pa-col-drawer');
    if (
      panel?.classList.contains('is-open') &&
      !e.target.closest('#pa-col-drawer') &&
      !e.target.closest('.pa-col-filters__btn')
    ) {
      closeDrawer();
    }
  }, true); // capture phase

  document.addEventListener('change', function (e) {
    const sortSel = e.target.closest('.pa-col-filters__sort');
    if (!sortSel) return;
    const value = sortSel.value;
    if (!value) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    applyUrl(url.pathname + url.search);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      const panel = document.querySelector('.pa-col-drawer');
      if (panel?.classList.contains('is-open')) closeDrawer();
    }
  });

  // Browser back/forward — re-fetch sections for the new URL
  window.addEventListener('popstate', function () {
    applyUrl(window.location.pathname + window.location.search);
  });

  /* ──────────────────────────────────────────────
     INIT
     ────────────────────────────────────────────── */
  function init() {
    // Body class can only be added once <body> exists.
    document.body.classList.add('template-posterarte-collection');

    discoverSections();
    initSticky();
    initFAQ();
    paintWishlistHearts();
    // Seed the POSTERARTE header wishlist counter on page load
    updateDawnCounter();
    // La sincronizacion con la cuenta puede cambiar la lista despues de pintar.
    document.addEventListener('pa:favoritos', function () {
      paintWishlistHearts();
      updateDawnCounter();
    });

    // After pagination navigation: scroll smoothly to the product grid
    if (sessionStorage.getItem('scrollToGrid') === 'true') {
      sessionStorage.removeItem('scrollToGrid');
      const gridEl = document.getElementById('pa-col-grid');
      if (gridEl) gridEl.scrollIntoView({ behavior: 'smooth' });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
